/**
 * UX audit D08 (ALL-5825), flow ADM-04 — human labels for the Admin Audit Trail.
 *
 * `/admin/audit-log` renders whatever the gateway stores: the Action column
 * prints the raw `action` verbatim (`API_KEY_VERIFICATION_ALLOWED`,
 * `BROWSER_LOGIN_ALLOWED`, `USER_INVITED`) and the Result cell prints the raw
 * `reasonCode`. Those are storage keys, not sentences, so the table reads as a
 * wall of SCREAMING_SNAKE_CASE.
 *
 * These two pure helpers turn a stored value into a short human phrase. They
 * are deliberately mechanical: a small table covers the handful of values whose
 * mechanical form is wrong or awkward, and everything else falls back to
 * sentence case of the underscore-separated words — so a new backend action
 * string renders readably without anyone having to extend a table, and nothing
 * is ever guessed or hidden. The raw value stays available to the caller for a
 * `title`, so no evidence is lost (see `features/admin/audit-trail.tsx`).
 *
 * No I/O, no React, no gateway call: this is display copy only.
 */

/** Shown when the gateway sent nothing at all. Matches the table's other blanks. */
const EMPTY_LABEL = "—";

/**
 * The stored values whose mechanical sentence case is wrong or misleading.
 * Everything here is a phrase a person would actually say; it is a display
 * label, never a re-encoding of the value's meaning.
 *
 * `API_KEY_VERIFICATION_ALLOWED` → sentence case would read "API key
 * verification allowed" (the event is the verification, not the key).
 * `BROWSER_LOGIN_*` → these are sign-in events from the user's point of view.
 */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  API_KEY_VERIFICATION_ALLOWED: "API key verified",
  API_KEY_VERIFICATION_DENIED: "API key denied",
  BROWSER_LOGIN_ALLOWED: "Signed in",
  BROWSER_LOGIN_DENIED: "Sign-in denied",
};

/**
 * `reasonCode` is an outcome-adjacent code (`ALLOW`/`DENY`/`ERROR`) or an
 * upper-snake detail code (`MFA_REQUIRED`). The three bare verdicts read as
 * adjectives next to a Result badge, so they get an explicit past-tense label;
 * every other code reads correctly as sentence case.
 */
const REASON_LABELS: Readonly<Record<string, string>> = {
  ALLOW: "Allowed",
  DENY: "Denied",
  ERROR: "Error",
};

/**
 * Initialisms and domain acronyms that must not be lowercased or
 * "Sentence-cased" into `Api`, `Mfa`, `Sso`. Kept to tokens that actually
 * appear in auth/audit action names; anything absent still renders readably,
 * just with its first letter capitalised.
 */
const ACRONYMS = new Set([
  "2FA",
  "API",
  "CSRF",
  "CSV",
  "DNS",
  "GCP",
  "HSM",
  "HTTP",
  "HTTPS",
  "ID",
  "IP",
  "JWT",
  "KMS",
  "MFA",
  "OIDC",
  "OTP",
  "PII",
  "RBAC",
  "SAML",
  "SIEM",
  "SSO",
  "SVC",
  "TLS",
  "TOTP",
  "TXI",
  "URL",
  "URI",
  "USR",
  "WLT",
]);

/**
 * "API_KEY_VERIFICATION_ALLOWED" → "API key verification allowed".
 * Only the first word is capitalised (sentence case); tokens in `ACRONYMS`
 * keep their casing.
 */
function sentenceCase(value: string): string {
  const parts = value
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => (ACRONYMS.has(part.toUpperCase()) ? part.toUpperCase() : part.toLowerCase()));
  if (parts.length === 0) return "";
  const [head, ...rest] = parts;
  const capitalised = ACRONYMS.has(head) ? head : head.charAt(0).toUpperCase() + head.slice(1);
  return [capitalised, ...rest].join(" ");
}

function labelFrom(
  value: string,
  known: Readonly<Record<string, string>>,
): string {
  const raw = (value ?? "").trim();
  if (!raw) return EMPTY_LABEL;
  // The gateway stores these upper case; normalising means a lower-case or
  // mixed-case value from a future writer still hits the table and the
  // acronym list rather than silently degrading to "api key".
  return known[raw.toUpperCase()] ?? sentenceCase(raw);
}

/** Human phrase for a stored audit `action`, e.g. `BROWSER_LOGIN_ALLOWED` → "Signed in". */
export function auditActionLabel(action: string): string {
  return labelFrom(action, ACTION_LABELS);
}

/** Human phrase for a stored audit `reasonCode`, e.g. `DENY` → "Denied", empty → "—". */
export function auditReasonLabel(reasonCode: string): string {
  return labelFrom(reasonCode, REASON_LABELS);
}
